import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { useAppServices } from "../../../hook/services";

// Where the Connect button stashed the install context before handing the
// browser to ServiceM8 — used when ServiceM8 doesn't echo `state` back (it
// isn't documented, so it can't be relied on).
export const SERVICEM8_OAUTH_CONTEXT_KEY = "servicem8_oauth_context";

const decodeState = (state) => {
  try {
    return JSON.parse(atob(state));
  } catch (err) {
    return null;
  }
};

const readStoredContext = () => {
  try {
    return JSON.parse(
      window.localStorage.getItem(SERVICEM8_OAUTH_CONTEXT_KEY) || "null"
    );
  } catch (err) {
    return null;
  }
};

const ServiceM8Callback = () => {
  const { search } = useLocation();
  const AppService = useAppServices();
  const [status, setStatus] = useState("connecting");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error_description") || params.get("error");

    const finish = (nextStatus, nextMessage, payload) => {
      setStatus(nextStatus);
      setMessage(nextMessage);

      // The Connect page opened this as a popup and is waiting to refresh its
      // own connection state.
      if (window.opener) {
        window.opener.postMessage(
          {
            message: "SERVICEM8_OAUTH_RESULT",
            success: nextStatus === "connected",
            error: nextStatus === "connected" ? null : nextMessage,
            payload: payload || null,
          },
          "*"
        );
        setTimeout(() => window.close(), 1500);
      }
    };

    const exchange = async () => {
      if (oauthError) {
        return finish("error", oauthError);
      }

      if (!code) {
        return finish(
          "error",
          "ServiceM8 didn't send an authorization code back."
        );
      }

      const context = (state && decodeState(state)) || readStoredContext();

      if (!context?.appId || !context?.locationId) {
        return finish(
          "error",
          "Couldn't tell which install this connection belongs to. Start the connection again from the app."
        );
      }

      const { response, error } = await AppService.servicem8.get_token({
        appId: context.appId,
        payload: {
          code,
          extras: {
            locationId: context.locationId,
            companyId: context.companyId,
          },
        },
      });

      console.log("ServiceM8 connect result:", response || error);

      if (!response?.success) {
        return finish(
          "error",
          error?.message || "Could not complete the ServiceM8 connection."
        );
      }

      window.localStorage.removeItem(SERVICEM8_OAUTH_CONTEXT_KEY);

      return finish(
        "connected",
        "ServiceM8 is connected. You can close this window.",
        response.data
      );
    };

    exchange();
  }, []);

  const view = {
    connecting: {
      icon: (
        <CircleNotch size={40} weight="bold" className="text-primary spinner" />
      ),
      title: "Connecting ServiceM8…",
      body: "Exchanging the authorization code, one moment.",
      tone: "text-primary",
    },
    connected: {
      icon: <CheckCircle size={40} weight="fill" className="text-success" />,
      title: "ServiceM8 connected",
      body: message,
      tone: "text-success",
    },
    error: {
      icon: <WarningCircle size={40} weight="fill" className="text-danger" />,
      title: "Connection failed",
      body: message,
      tone: "text-danger",
    },
  }[status];

  return (
    <div className="d-flex align-items-center justify-content-center bg-light min-vh-100 p-4">
      <div
        className="card border-0 shadow-sm rounded-4 text-center"
        style={{ maxWidth: 460, width: "100%" }}
      >
        <div className="card-body" style={{ padding: "2.5rem" }}>
          <div className="mb-3">{view.icon}</div>
          <h5 className={`fw-bold mb-2 ${view.tone}`}>{view.title}</h5>
          <p className="text-muted mb-0 small">{view.body}</p>
        </div>
      </div>

      <style>{`
        .spinner { animation: servicem8-spin 1s linear infinite; }
        @keyframes servicem8-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ServiceM8Callback;
